import Cookies from 'js-cookie';

/**
 * Web Crypto AES-GCM encrypt/decrypt for chatbot localStorage.
 * Auth users: derive key from login_token (never hardcoded).
 * Guests: derive key from a randomly generated browser secret.
 */

const GUEST_SECRET_STORAGE_KEY = 'quickvee_chatbot_guest_secret';
const HKDF_SALT = 'quickvee-chatbot-aes-v1';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** @type {Map<string, Promise<CryptoKey>>} */
const derivedKeyCache = new Map();

const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

const base64ToBytes = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const fingerprintSecret = async (secret, merchantId, emailId) => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    textEncoder.encode(`${secret}|${merchantId}|${emailId}`),
  );
  return bytesToBase64(new Uint8Array(digest)).slice(0, 32);
};

export const getOrCreateGuestEncryptionSecret = () => {
  try {
    const existing = localStorage.getItem(GUEST_SECRET_STORAGE_KEY);
    if (existing && String(existing).trim()) {
      return String(existing).trim();
    }
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const secret = bytesToBase64(bytes);
    localStorage.setItem(GUEST_SECRET_STORAGE_KEY, secret);
    return secret;
  } catch {
    return null;
  }
};

export const clearGuestEncryptionSecret = () => {
  try {
    localStorage.removeItem(GUEST_SECRET_STORAGE_KEY);
    derivedKeyCache.clear();
    return true;
  } catch {
    return false;
  }
};

/** Invalidate cached CryptoKeys (e.g. after token rotation / logout). */
export const invalidateChatbotEncryptionKeyCache = () => {
  derivedKeyCache.clear();
};

/**
 * Resolve key material for encrypt/decrypt.
 * Non-empty email_id requires a valid login_token; otherwise null.
 */
export const resolveChatbotEncryptionSecret = (emailId) => {
  const email = emailId == null ? '' : String(emailId).trim();
  if (email) {
    const token = String(Cookies.get('login_token') || '').trim();
    return token || null;
  }
  return getOrCreateGuestEncryptionSecret();
};

const deriveAesGcmKey = async (secret, merchantId, emailId) => {
  if (!secret || !merchantId) {
    throw new Error('Missing encryption secret or merchant.');
  }

  const cacheKey = await fingerprintSecret(secret, merchantId, emailId);
  const cached = derivedKeyCache.get(cacheKey);
  if (cached) return cached;

  const derivePromise = (async () => {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      textEncoder.encode(secret),
      'HKDF',
      false,
      ['deriveKey'],
    );

    return crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: textEncoder.encode(HKDF_SALT),
        info: textEncoder.encode(`${merchantId}\0${emailId ?? ''}`),
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  })();

  derivedKeyCache.set(cacheKey, derivePromise);
  try {
    return await derivePromise;
  } catch (error) {
    derivedKeyCache.delete(cacheKey);
    throw error;
  }
};

export const encryptChatbotPayload = async (
  secret,
  merchantId,
  emailId,
  payload,
) => {
  const key = await deriveAesGcmKey(secret, merchantId, emailId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = textEncoder.encode(JSON.stringify(payload));
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintext,
  );

  return {
    v: 2,
    alg: 'AES-GCM',
    iv: bytesToBase64(iv),
    data: bytesToBase64(new Uint8Array(cipherBuffer)),
  };
};

export const decryptChatbotPayload = async (
  secret,
  merchantId,
  emailId,
  record,
) => {
  if (!record || record.v !== 2 || record.alg !== 'AES-GCM') {
    return null;
  }
  if (!record.iv || !record.data) {
    return null;
  }

  try {
    const key = await deriveAesGcmKey(secret, merchantId, emailId);
    const iv = base64ToBytes(record.iv);
    const ciphertext = base64ToBytes(record.data);
    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext,
    );
    const parsed = JSON.parse(textDecoder.decode(plainBuffer));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};
